/**
 * Tally Role Configurations
 *
 * Defines hardcoded roles and their specific table layouts/columns
 * for the job tallies system. Each role can have different table
 * variations to accommodate specific data collection needs.
 */

/**
 * Base column types available for role tables
 */
const COLUMN_TYPES = {
	STAFF_NUMBER: 'staff_number',
	STAFF_NAME: 'staff_name',
	RUN_COUNT: 'run_count',
	DAY_TALLY: 'day_tally',
	TRAVEL: 'travel',
	HOURS: 'hours',
	RATE: 'rate',
	AMOUNT: 'amount',
	NOTES: 'notes'
};

/**
 * Pre-defined roles with their table configurations
 */
const TALLY_ROLES = {
	'shearing_crutching': {
		id: 'shearing_crutching',
		name: 'Shearing & Crutching',
		displayOrder: 1,
		color: 'primary',
		hasSections: true,  // This role has SHEARING and CRUTCHING sections
		sections: ['SHEARING', 'CRUTCHING'],
		columns: [
			{
				type: COLUMN_TYPES.STAFF_NUMBER,
				label: '#',
				width: 'auto',
				readonly: true
			},
			{
				type: COLUMN_TYPES.STAFF_NAME,
				label: 'Staff Name',
				width: '200px',
				readonly: true
			},
			{
				type: COLUMN_TYPES.RUN_COUNT,
				label: 'Run Count',
				width: '100px',
				inputType: 'number',
				canAddRuns: true,
				defaultRuns: [
					{id: '1', name: 'Run 1', notes: ''},
					{id: '2', name: 'Run 2', notes: ''},
					{id: '3', name: 'Run 3', notes: ''},
					{id: '4', name: 'Run 4', notes: ''}
				]
			},
			{
				type: COLUMN_TYPES.DAY_TALLY,
				label: 'DAY TOTAL',
				width: '100px',
				readonly: true,
				calculated: true
			},
			{
				type: 'mess',
				label: 'Mess',
				width: '80px',
				inputType: 'checkbox'
			},
			{
				type: 'evos',
				label: "Evo's",
				width: '80px',
				inputType: 'checkbox'
			}
		],
		summaryColumns: ['travel', 'total'],
		hasRunNotes: true,
		allowMultipleRuns: true
	},

	'presser': {
		id: 'presser',
		name: 'Presser',
		displayOrder: 2,
		color: 'success',
		hasSections: false,
		columns: [
			{
				type: COLUMN_TYPES.STAFF_NAME,
				label: 'Presser',
				width: '200px',
				readonly: true
			},
			{
				type: 'total_runs',
				label: 'Total Runs',
				width: '100px',
				inputType: 'number'
			},
			{
				type: 'total_bales',
				label: 'Total Bales',
				width: '100px',
				inputType: 'number'
			},
			{
				type: 'total_kilo',
				label: 'Total Kilo',
				width: '100px',
				inputType: 'number'
			},
			{
				type: COLUMN_TYPES.DAY_TALLY,
				label: 'DAY TOTAL',
				width: '100px',
				readonly: true,
				calculated: true
			},
			{
				type: 'mess',
				label: 'Mess',
				width: '80px',
				inputType: 'checkbox'
			},
			{
				type: COLUMN_TYPES.TRAVEL,
				label: 'Travel',
				width: '80px',
				inputType: 'number'
			}
		],
		summaryColumns: ['total'],
		hasRunNotes: false,
		allowMultipleRuns: true,
		addButtonText: 'Add Other'
	},

	'woolhandler': {
		id: 'woolhandler',
		name: 'Woolhandler',
		displayOrder: 3,
		color: 'info',
		hasSections: false,
		columns: [
			{
				type: COLUMN_TYPES.STAFF_NAME,
				label: 'Woolhandler',
				width: '200px',
				readonly: true
			},
			{
				type: 'total_runs',
				label: 'Total Runs',
				width: '100px',
				inputType: 'number'
			},
			{
				type: COLUMN_TYPES.DAY_TALLY,
				label: 'DAY TOTAL',
				width: '100px',
				readonly: true,
				calculated: true
			},
			{
				type: 'mess',
				label: 'Mess',
				width: '80px',
				inputType: 'checkbox'
			},
			{
				type: COLUMN_TYPES.TRAVEL,
				label: 'Travel',
				width: '80px',
				inputType: 'number'
			}
		],
		summaryColumns: ['total'],
		hasRunNotes: false,
		allowMultipleRuns: true,
		addButtonText: 'Add Other'
	},

	'classer': {
		id: 'classer',
		name: 'Classer',
		displayOrder: 4,
		color: 'warning',
		hasSections: false,
		columns: [
			{
				type: COLUMN_TYPES.STAFF_NAME,
				label: 'Classer',
				width: '200px',
				readonly: true
			},
			{
				type: 'total_runs',
				label: 'Total Runs',
				width: '100px',
				inputType: 'number'
			},
			{
				type: 'total_fleeces',
				label: 'Total Fleeces',
				width: '100px',
				inputType: 'number'
			},
			{
				type: COLUMN_TYPES.DAY_TALLY,
				label: 'DAY TOTAL',
				width: '100px',
				readonly: true,
				calculated: true
			},
			{
				type: 'mess',
				label: 'Mess',
				width: '80px',
				inputType: 'checkbox'
			},
			{
				type: COLUMN_TYPES.TRAVEL,
				label: 'Travel',
				width: '80px',
				inputType: 'number'
			}
		],
		summaryColumns: ['total'],
		hasRunNotes: false,
		allowMultipleRuns: true,
		addButtonText: 'Add Other'
	},

	'sheepo': {
		id: 'sheepo',
		name: 'Sheepo',
		displayOrder: 5,
		color: 'info',
		hasSections: false,
		columns: [
			{
				type: COLUMN_TYPES.STAFF_NAME,
				label: 'Sheepo',
				width: '200px',
				readonly: true
			},
			{
				type: 'total_runs',
				label: 'Total Runs',
				width: '100px',
				inputType: 'number'
			},
			{
				type: COLUMN_TYPES.DAY_TALLY,
				label: 'DAY TOTAL',
				width: '100px',
				readonly: true,
				calculated: true
			},
			{
				type: 'mess',
				label: 'Mess',
				width: '80px',
				inputType: 'checkbox'
			},
			{
				type: COLUMN_TYPES.TRAVEL,
				label: 'Travel',
				width: '80px',
				inputType: 'number'
			}
		],
		summaryColumns: ['total'],
		hasRunNotes: false,
		allowMultipleRuns: true,
		addButtonText: 'Add Other'
	},

	'cook': {
		id: 'cook',
		name: 'Cook',
		displayOrder: 6,
		color: 'warning',
		hasSections: false,
		columns: [
			{
				type: COLUMN_TYPES.STAFF_NAME,
				label: 'Cook',
				width: '200px',
				readonly: true
			},
			{
				type: 'payment_type',
				label: 'Type',
				width: '150px',
				inputType: 'select',
				options: [
					{value: 'per_day', label: 'Per Day'},
					{value: 'per_head', label: 'Per Head'}
				]
			},
			{
				type: 'count',
				label: 'Count',
				width: '100px',
				inputType: 'number'
			},
			{
				type: COLUMN_TYPES.DAY_TALLY,
				label: 'DAY TOTAL',
				width: '100px',
				readonly: true,
				calculated: true
			},
			{
				type: 'mess',
				label: 'Mess',
				width: '80px',
				inputType: 'checkbox'
			},
			{
				type: COLUMN_TYPES.TRAVEL,
				label: 'Travel',
				width: '80px',
				inputType: 'number'
			}
		],
		summaryColumns: ['total'],
		hasRunNotes: false,
		allowMultipleRuns: true,
		addButtonText: 'Add Other'
	}
};

/**
 * Get all roles sorted by display order
 */
export function getAllRoles() {
	return Object.values(TALLY_ROLES).sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Get role configuration by ID
 */
export function getRoleById(roleId) {
	return TALLY_ROLES[roleId] || null;
}

/**
 * Get default staff entry for a role
 */
export function getDefaultStaffEntry(roleId, staffId, staffName) {
	const role = getRoleById(roleId);
	if (!role) return null;

	const entry = {
		staff_id: staffId,
		staff_name: staffName,
		travel: false
	};

	// Add role-specific default values
	if (role.columns.some(col => col.type === COLUMN_TYPES.RUN_COUNT)) {
		entry.runs = role.columns.find(col => col.type === COLUMN_TYPES.RUN_COUNT).defaultRuns.map(run => ({
			run: run.id,
			count: '0'
		}));
	}

	if (role.columns.some(col => col.type === COLUMN_TYPES.HOURS)) {
		entry.hours = '0';
	}

	if (role.columns.some(col => col.type === COLUMN_TYPES.RATE)) {
		entry.rate = '0.00';
	}

	if (role.columns.some(col => col.type === COLUMN_TYPES.NOTES)) {
		entry.notes = '';
	}

	// Add mess, evos, and travel fields for shearing_crutching, presser, woolhandler, classer, and sheepo roles
	if (roleId === 'shearing_crutching' || roleId === 'presser' || roleId === 'woolhandler' || roleId === 'classer' || roleId === 'sheepo') {
		entry.mess = 0;
		entry.evos = 0;
		entry.travel = 0;
		entry.day_tally = 0;
	}

	// Add presser-specific fields
	if (roleId === 'presser') {
		entry.total_runs = 0;
		entry.total_bales = 0;
		entry.total_kilo = 0;
	}

	// Add woolhandler-specific fields
	if (roleId === 'woolhandler') {
		entry.total_runs = 0;
	}

	// Add classer-specific fields
	if (roleId === 'classer') {
		entry.total_runs = 0;
		entry.total_fleeces = 0;
	}

	// Add sheepo-specific fields
	if (roleId === 'sheepo') {
		entry.total_runs = 0;
	}

	// Add cook-specific fields
	if (roleId === 'cook') {
		entry.payment_type = 'per_day';
		entry.count = 0;
		entry.mess = 0;
		entry.travel = 0;
		entry.day_tally = 0;
	}

	return entry;
}

/**
 * Initialize default tallies data structure with hardcoded roles
 */
export function initializeDefaultTalliesData(jobDates = []) {
	const roles = getAllRoles();

	return {
		roles: roles.map(role => ({
			role_id: role.id,
			role_name: role.name
		})),
		tally_data: jobDates.map(date => ({
			date_verbose: date.date_verbose,
			date: date.date,
			roles: roles.map(role => ({
				role_id: role.id,
				role_name: role.name,
				runs: role.allowMultipleRuns ? (role.columns.find(col => col.type === COLUMN_TYPES.RUN_COUNT)?.defaultRuns || []) : [],
				staff_counts: []
			}))
		}))
	};
}

/**
 * Calculate computed values for a staff entry
 */
export function calculateStaffValues(roleId, staffEntry) {
	const role = getRoleById(roleId);
	if (!role) return staffEntry;

	const calculated = {...staffEntry};

	// Calculate day tally from runs
	if (calculated.runs && Array.isArray(calculated.runs)) {
		let total = 0;
		calculated.runs.forEach(run => {
			const count = parseFloat(run.count) || 0;
			total += count;
		});
		calculated.day_tally = total;
	}

	// Calculate amount from hours * rate
	if (calculated.hours !== undefined && calculated.rate !== undefined) {
		const hours = parseFloat(calculated.hours) || 0;
		const rate = parseFloat(calculated.rate) || 0;
		calculated.amount = (hours * rate).toFixed(2);
	}

	return calculated;
}

export {COLUMN_TYPES, TALLY_ROLES};